// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, { useState } from 'react';
import clsx from 'clsx';
import {
    ChoiceGroup,
    IChoiceGroupOption,
} from 'office-ui-fabric-react/lib/ChoiceGroup';
import {
    Dialog,
    DialogFooter,
    DialogType,
} from 'office-ui-fabric-react/lib/Dialog';
import { getId } from 'office-ui-fabric-react/lib/Utilities';
import { useTranslation } from 'react-i18next';

import { logPageAction } from 'src/lib/ApplicationInsights';

import Styles from './styles.module.scss';

interface AddressChoicesType {
    options: IChoiceGroupOption[];
    onCancelChoiceClicked: () => void;
    onChoiceChange?: (
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption
    ) => void;
    onSaveChoiceClicked: () => void;
    onDismissed: () => void;
}

export default function AddressVerification(props: AddressChoicesType) {
    const [hide, hideDialog] = useState(false);
    const [isSaved, save] = useState(false);

    const { t } = useTranslation();

    function closeDialog() {
        logPageAction('Address validation back button clicked');
        props.onCancelChoiceClicked();
        hideDialog(true);
    }

    function saveAndClose() {
        logPageAction('Address validation next button clicked');
        props.onSaveChoiceClicked();
        hideDialog(true);
        save(true);
    }

    const onDismissed = () => {
        logPageAction('Address verification popup is dismissed');
        if (isSaved) {
            props.onDismissed();
        }
    };

    const dialogOptions = props.options.map(
        (option: { key: string; text: string }) =>
            ({ key: option.key, text: option.text } as IChoiceGroupOption)
    );

    const titleId: string = getId('dialogLabel');
    const subTextId: string = getId('subTextLabel');

    return (
        <section>
            <Dialog
                hidden={hide}
                onDismiss={closeDialog}
                dialogContentProps={{
                    titleId,
                    subTextId,
                    type: DialogType.normal,
                    closeButtonAriaLabel: t('close'),
                    title: t('address_validation'),
                    subText: t('address_validation_message'),
                }}
                modalProps={{
                    onDismissed,
                    titleAriaId: titleId,
                    subtitleAriaId: subTextId,
                    isBlocking: true,
                    containerClassName: `${Styles.popup}`,
                }}
            >
                <div>
                    <ChoiceGroup
                        className="defaultChoiceGroup"
                        options={dialogOptions}
                        onChange={props.onChoiceChange}
                        label={t('address_validation_select')}
                        required={true}
                    />
                    <p>{t('address_validation_select_continue')}</p>
                </div>
                <DialogFooter>
                    <button
                        name="backBtn"
                        className={clsx(Styles.buttonSecondary, Styles.button)}
                        onClick={closeDialog}
                    >
                        {t('back')}
                    </button>
                    <button
                        name="nextBtn"
                        className={clsx(Styles.buttonPrimary, Styles.button)}
                        onClick={saveAndClose}
                    >
                        {t('next')}
                    </button>
                </DialogFooter>
            </Dialog>
        </section>
    );
}
